import React, { useEffect } from "react";
import classNames from "classnames/bind";
import styles from "../../AddJobModal.module.scss";
import { JobFormData, JobFormErrors } from "../../types/add-job.types";
import {
  JobTypeOptions,
  PolicyTypeOptions,
  PriorityOptions,
} from "../../constants/add-job.constants";
import { ISalesRep } from "~/apis/userManagement";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";

const cx = classNames.bind(styles);

interface JobBasicInfoProps {
  formData: JobFormData;
  errors: JobFormErrors;
  salesReps: ISalesRep[];
  customerName?: string;
  onFieldChange: (field: keyof JobFormData, value: any) => void;
  onErrorFieldChange: (field: keyof JobFormErrors, value: string) => void;
}

const generateJobNumber = () => {
  const year = new Date().getFullYear();
  const randomNum = Math.floor(Math.random() * 1000)
    .toString()
    .padStart(3, "0");
  return `JOB-${year}-${randomNum}`;
};

const JobBasicInfo: React.FC<JobBasicInfoProps> = ({
  formData,
  errors,
  salesReps,
  customerName = "",
  onFieldChange,
  onErrorFieldChange,
}) => {
  const generateAndSetJobName = () => {
    if (customerName && formData.jobNumber) {
      // Extract last name from customer name (assuming format: "FirstName LastName")
      const nameParts = customerName.trim().split(/\s+/);
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : customerName;

      const autoJobName = `${lastName} - ${formData.jobNumber}`;
      onFieldChange("jobName", autoJobName);
    }
  };

  // Auto-generate job number on component mount if empty
  useEffect(() => {
    if (!formData.jobNumber) {
      const newJobNumber = generateJobNumber();
      onFieldChange("jobNumber", newJobNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-generate job name based on customer name and job number
  useEffect(() => {
    if (!formData.jobName && customerName && formData.jobNumber) {
      generateAndSetJobName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName, formData.jobNumber]);

  const handleJobNumberChange = (value: string) => {
    onFieldChange("jobNumber", value);
    onErrorFieldChange("jobNumber", "");
  };

  const handleJobTypeChange = (value: string) => {
    const selectedType = JobTypeOptions.find((opt) => opt.value === value);
    onFieldChange("jobType", selectedType?.value || "");
    if (!selectedType?.value) {
      onErrorFieldChange("jobType", "Job type is required");
    } else {
      onErrorFieldChange("jobType", "");
    }
  };

  const handlePriorityChange = (value: string) => {
    const selectedPriority = PriorityOptions.find((opt) => opt.value === value);
    onFieldChange("priority", selectedPriority?.value || "");
    onErrorFieldChange("priority", "");
  };

  const handleSalesRepChange = (value: string) => {
    const selectedRep = salesReps.find((opt) => opt.id.toString() === value);
    onFieldChange("salesRepId", selectedRep?.id.toString() || "");
    if (!selectedRep?.id.toString()) {
      onErrorFieldChange("salesRepId", "Sales rep is required");
    } else {
      onErrorFieldChange("salesRepId", "");
    }
  };

  return (
    <div className={cx("job-basic-info-container", "pt-[31px] pr-[25px]")}>
      <div className={cx("row")}>
        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Job Number (Optional)</h4>
          <div className={cx("job-number-input-wrapper")}>
            <PrimaryInput
              name="jobNumber"
              placeholder="Enter job number"
              value={formData.jobNumber}
              onChange={(e) => handleJobNumberChange(e.target.value)}
              className={cx("input")}
              error={!!errors.jobNumber}
            />
            <button
              className={cx("auto-button", "inside-input")}
              onClick={() => {
                const newJobNumber = generateJobNumber();
                onFieldChange("jobNumber", newJobNumber);
              }}
            >
              Auto
            </button>
          </div>
          {errors.jobNumber && (
            <p className={cx("error-message")}>{errors.jobNumber}</p>
          )}
        </div>

        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Job Name (Optional)</h4>
          <div className={cx("job-number-input-wrapper")}>
            <PrimaryInput
              name="jobName"
              placeholder="Enter job name"
              value={formData.jobName}
              onChange={(e) => {
                onFieldChange("jobName", e.target.value);
                onErrorFieldChange("jobName", "");
              }}
              className={cx("input")}
              error={!!errors.jobName}
            />
            <button
              className={cx("auto-button", "inside-input")}
              onClick={generateAndSetJobName}
              disabled={!customerName || !formData.jobNumber}
            >
              Auto
            </button>
          </div>
          {errors.jobName && (
            <p className={cx("error-message")}>{errors.jobName}</p>
          )}
        </div>
      </div>

      <div className={cx("row")}>
        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Job Type</h4>

          <PrimarySelectInput
            name="jobType"
            placeholder="Select Job Type"
            value={
              JobTypeOptions.find((opt) => opt.value === formData.jobType)
                ?.label || ""
            }
            onChange={(value) => handleJobTypeChange(value as string)}
            options={JobTypeOptions}
            className={cx("select-input")}
            errorMessage={errors.jobType}
          />
        </div>

        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Priority (Optional)</h4>

          <PrimarySelectInput
            name="priority"
            placeholder="Select Priority"
            value={
              PriorityOptions.find((opt) => opt.value === formData.priority)
                ?.label || ""
            }
            onChange={(value) => handlePriorityChange(value as string)}
            options={PriorityOptions}
            className={cx("select-input")}
            errorMessage={errors.priority}
          />
        </div>
      </div>

      {/* Insurance Fields - Only show when Job Type is Insurance */}
      {formData.jobType === "Insurance" && (
        <div className={cx("job-basic-info-container", "!w-full")}>
          <div className={cx("row")}>
            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Insurance company Name (Optional)</h4>
              <PrimaryInput
                name="insuranceCompanyName"
                placeholder="Enter Insurance company name"
                value={formData.insuranceCompanyName}
                onChange={(e) => {
                  onFieldChange("insuranceCompanyName", e.target.value);
                  onErrorFieldChange("insuranceCompanyName", "");
                }}
                className={cx("input")}
                errorMessage={errors.insuranceCompanyName}
              />
            </div>

            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Email (Optional)</h4>
              <PrimaryInput
                name="insuranceEmail"
                placeholder="Enter email"
                value={formData.insuranceEmail}
                onChange={(e) => {
                  onFieldChange("insuranceEmail", e.target.value);
                  if (
                    e.target.value &&
                    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value)
                  ) {
                    onErrorFieldChange("insuranceEmail", "Email is invalid");
                  } else {
                    onErrorFieldChange("insuranceEmail", "");
                  }
                }}
                className={cx("input")}
                errorMessage={errors.insuranceEmail}
              />
            </div>
          </div>

          <div className={cx("row")}>
            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Claim Number (Optional)</h4>
              <PrimaryInput
                name="claimNumber"
                placeholder="Enter claim number"
                value={formData.claimNumber}
                onChange={(e) => {
                  onFieldChange("claimNumber", e.target.value);
                  if (e.target.value && !/^\d+$/.test(e.target.value)) {
                    onErrorFieldChange(
                      "claimNumber",
                      "Claim number must be numeric"
                    );
                  } else {
                    onErrorFieldChange("claimNumber", "");
                  }
                }}
                className={cx("input")}
                errorMessage={errors.claimNumber}
              />
            </div>

            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Phone Number (Optional)</h4>
              <PrimaryInput
                name="insurancePhoneNumber"
                placeholder="Enter phone"
                value={formData.insurancePhoneNumber}
                onChange={(e) => {
                  onFieldChange("insurancePhoneNumber", e.target.value);
                  if (e.target.value && !/^\d+$/.test(e.target.value)) {
                    onErrorFieldChange(
                      "insurancePhoneNumber",
                      "Phone number must be numeric"
                    );
                  } else {
                    onErrorFieldChange("insurancePhoneNumber", "");
                  }
                }}
                className={cx("input")}
                errorMessage={errors.insurancePhoneNumber}
              />
            </div>
          </div>

          <div className={cx("row")}>
            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Adjuster Name (Optional)</h4>
              <PrimaryInput
                name="adjusterName"
                placeholder="Enter adjuster name"
                value={formData.adjusterName}
                onChange={(e) => onFieldChange("adjusterName", e.target.value)}
                className={cx("input")}
              />
            </div>

            <div className={cx("item")}>
              <h4 className={cx("item-title")}>Policy Type (Optional)</h4>
              <PrimarySelectInput
                name="policyType"
                placeholder="Select policy"
                options={PolicyTypeOptions.map((option) => ({
                  label: option.label,
                  value: option.value,
                }))}
                value={
                  PolicyTypeOptions.find(
                    (opt) => opt.value === formData.policyType
                  )?.label || ""
                }
                onChange={(value) => {
                  onFieldChange("policyType", value as string);
                  onErrorFieldChange("policyType", "");
                }}
                className={cx("select-input")}
                errorMessage={errors.policyType}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sales Rep - Always show */}
      <div className={cx("row")}>
        <div className={cx("item")}>
          <h4 className={cx("item-title")}>Sales Rep</h4>
          <PrimarySelectInput
            name="salesRepId"
            placeholder="Select"
            options={salesReps.map((option) => ({
              label: option.username,
              value: option.id.toString(),
            }))}
            value={
              salesReps.find((rep) => rep.id.toString() === formData.salesRepId)
                ?.username || ""
            }
            onChange={(value) => handleSalesRepChange(value as string)}
            className={cx("select-input")}
            errorMessage={errors.salesRepId}
          />
        </div>
      </div>
    </div>
  );
};

export default JobBasicInfo;
